/**
 * /orders/:orderId — live tracker for a single order. Listens for
 * `order:status_change` over Socket.io (via useOrder) so the status
 * updates the moment the barista advances the order in the kitchen.
 */
import { Link, useParams } from 'react-router-dom';
import { formatOrderNumber, formatPrice, ORDER_STATUS_LABELS } from '@cracked-mug/shared';
import { OrderSummary, type OrderSummaryLine } from '../components/orders/OrderSummary';
import { StatusTracker } from '../components/orders/StatusTracker';
import { useOrder } from '../hooks/useOrders';

export function OrderTracking() {
  const params = useParams<{ orderId: string }>();
  const orderId = params.orderId;
  const { order, isLoading, error } = useOrder(orderId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="font-mono text-sm text-wca-gray-mid">Loading order…</span>
      </div>
    );
  }

  if (error !== null || order === null) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-6">
        <p className="font-display text-2xl">Couldn&rsquo;t load the order</p>
        <p className="text-sm text-wca-gray-mid mt-2 max-w-md">{error ?? 'Order not found.'}</p>
        <Link to="/order" className="mt-6 text-wca-red hover:underline font-medium">
          ← Back to menu
        </Link>
      </div>
    );
  }

  const summaryLines: OrderSummaryLine[] = order.items.map((item) => ({
    key: `${item.itemId}-${item.name}`,
    name: item.name,
    quantity: item.quantity,
    basePriceCents: item.basePriceCents,
    selectedCustomizations: item.selectedCustomizations,
    specialInstructions: item.specialInstructions,
  }));

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <header className="text-center">
        <p className="font-mono text-xs text-wca-gray-mid uppercase tracking-widest">Order</p>
        <h1 className="font-display text-6xl mt-1 tabular-nums">
          {formatOrderNumber(order.orderNumber)}
        </h1>
        <p className="text-wca-gray-mid mt-2">
          {order.status === 'completed'
            ? order.fulfillmentMethod === 'pickup'
              ? 'Picked up — thanks!'
              : 'Delivered — enjoy!'
            : order.status === 'cancelled'
              ? 'Cancelled'
              : order.fulfillmentMethod === 'pickup'
                ? `Ready for pickup in ~${order.estimatedMinutes} min`
                : `Delivering to room ${order.teacherRoom} in ~${order.estimatedMinutes} min`}
        </p>
      </header>

      <StatusTracker status={order.status} />

      <OrderSummary
        items={summaryLines}
        subtotalCents={order.subtotalCents}
        taxCents={order.taxCents}
        tipCents={order.tipCents}
        totalCents={order.totalCents}
      />

      {order.paymentStatus === 'unpaid' && order.status !== 'cancelled' && (
        <div className="bg-wca-red-light rounded-card p-4 text-sm" role="status">
          <p className="font-medium text-wca-red-dark">Payment incomplete</p>
          <p className="text-wca-red-dark mt-1">
            We placed your order but the payment didn&rsquo;t go through. Total{' '}
            <span className="font-mono">{formatPrice(order.totalCents)}</span>. Tap an admin to
            resolve.
          </p>
        </div>
      )}

      <div className="text-center space-y-2">
        <Link to={`/order`} className="inline-block font-medium text-wca-red hover:underline">
          Order something else →
        </Link>
        <p className="text-xs text-wca-gray-mid">
          Status: <span className="font-mono">{ORDER_STATUS_LABELS[order.status]}</span>
        </p>
      </div>
    </div>
  );
}
